/**
 * PUBLIC FREE-TEXT REDACTION — single source of truth.
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS (P0 incident, reported 2026-09-22)
 *
 * A contractor opened the $29 painting lead in Fontana, CA WITHOUT buying it
 * and could read the customer's phone number. The structured contact block was
 * never the leak: `mkt_listing_contacts` is correctly locked by RLS to the
 * owner, a buyer with a purchase row, or an admin, and the `browse-listings`
 * column allow-list has no contact columns at all.
 *
 * The leak was the FREE TEXT. `title` and `description` are public by design
 * (they are what a contractor reads before deciding to buy), and the poster had
 * typed "call me at 909-555-0143" straight into the description. The browse
 * endpoint returned that string verbatim to everyone — signed out included.
 * AdminModeration only DETECTED these after the fact for a human to clean up;
 * nothing stopped them rendering in the meantime.
 *
 * So the lead lock was being bypassed in plain sight, by the one field nobody
 * was filtering.
 *
 * ---------------------------------------------------------------------------
 * DESIGN
 *
 * This module is deliberately dependency-free and pure so the SAME rules can
 * run in three places (defence in depth — any one layer alone would be enough,
 * but a single bug must not re-open the leak):
 *
 *   1. SERVER (authoritative) — `browse-listings` scrubs title/description
 *      before the payload leaves the edge function, so the digits never reach
 *      an unentitled browser, a cache, a CDN, a crawler or a scraper.
 *   2. CLIENT (render guard) — JobCard / JobDetail scrub again at paint time
 *      for any listing the viewer has not unlocked, so a stale cached payload
 *      or a future code path cannot render a number.
 *   3. WRITE TIME (prevention) — ListingForm warns the poster and stores the
 *      contact details in the locked contact record instead.
 *
 * WHAT IS NEVER REDACTED: text shown to someone who HAS the entitlement. Once
 * a contractor buys the lead they must see everything the customer wrote —
 * `redactPublicText` is only ever called on the locked view.
 *
 * FALSE POSITIVES ARE ACCEPTABLE, FALSE NEGATIVES ARE NOT. When a pattern is
 * ambiguous this module removes it. A mangled budget line costs a contractor
 * nothing; a leaked phone number costs the marketplace its entire business
 * model, because the buyer can just call the customer for free.
 */

/** What a removed span is replaced with, per language. */
export const REDACTION_MARK = {
  en: '[contact hidden]',
  es: '[contacto oculto]',
} as const;

export type RedactLang = keyof typeof REDACTION_MARK;

/**
 * US/CA phone numbers, 10 or 11 digits, in any common separator style:
 *   9095550143 · 909-555-0143 · (909) 555-0143 · 909.555.0143 · +1 909 555 0143
 *
 * Requiring TEN digits (rather than seven) is what keeps budgets, square
 * footage, years and dimensions intact — "$5,000 - $15,000", "1200 sq ft",
 * "2026" and "24x36" all have fewer than ten consecutive phone-shaped digits.
 */
const PHONE_10 =
  /(?:\+?1[\s.\-–—]*)?(?:\(\s*\d{3}\s*\)|\d{3})[\s.\-–—]*\d{3}[\s.\-–—]*\d{4}(?!\d)/g;

/**
 * Seven-digit local numbers ("555-0143") are only redacted when the surrounding
 * words show contact intent, because 3-4 digit pairs also appear in perfectly
 * innocent budget ranges ("500-2000"). Bilingual trigger list.
 */
const PHONE_7_CONTEXT =
  /(call|text|phone|tel|telephone|cell|mobile|reach|contact|ring|llame|llamar|llámame|teléfono|telefono|celular|marque|contacto|escríbame|escribame)[^\n]{0,24}?(?<!\d)\d{3}[\s.\-–—]\d{4}(?!\d)/gi;

/** Email addresses, including the common "a (at) b (dot) com" evasion. */
const EMAIL = /[A-Za-z0-9._%+-]+\s*(?:@|\(\s*at\s*\)|\[\s*at\s*\]|\s+at\s+)\s*[A-Za-z0-9.-]+\s*(?:\.|\(\s*dot\s*\)|\[\s*dot\s*\]|\s+dot\s+)\s*[A-Za-z]{2,}/gi;

/** Web addresses and social handles — another way to hand over a contact. */
const URL = /\b(?:https?:\/\/|www\.)[^\s<>"')]+/gi;
const BARE_DOMAIN =
  /\b[A-Za-z0-9-]+\.(?:com|net|org|io|co|us|biz|info|shop|site|link|me)\b(?:\/[^\s<>"')]*)?/gi;
const SOCIAL_HANDLE = /(?:^|\s)@[A-Za-z0-9._]{3,}/g;

/**
 * Street addresses — "1234 Elm St", "23 Oak Avenue #4".
 * City and state stay public (every card shows "Fontana, CA"); it is the
 * precise doorstep that is part of what the $29 buys.
 */
const STREET =
  /\b\d{1,6}\s+(?:[NSEW]\.?\s+)?[A-Za-z0-9'.-]+(?:\s+[A-Za-z0-9'.-]+){0,3}\s+(?:st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|way|ct|court|pl|place|cir|circle|ter|terrace|pkwy|parkway|hwy|highway|calle|avenida|carretera)\b\.?(?:\s*(?:#|apt\.?|unit|ste\.?|suite)\s*[A-Za-z0-9-]+)?/gi;

/** 5 or 9 digit US ZIP, when explicitly labelled or standing alone as a token. */
const ZIP = /\b(?:zip|zipcode|postal(?:\s*code)?|cp)\s*:?\s*\d{5}(?:-\d{4})?\b/gi;

/**
 * Every rule, in application order. Longer/more specific patterns run first so
 * a street address is not half-eaten by the ZIP rule, etc.
 */
const RULES: Array<{ id: string; re: RegExp }> = [
  { id: 'email', re: EMAIL },
  { id: 'url', re: URL },
  { id: 'street', re: STREET },
  { id: 'phone', re: PHONE_10 },
  { id: 'phone7', re: PHONE_7_CONTEXT },
  { id: 'domain', re: BARE_DOMAIN },
  { id: 'zip', re: ZIP },
  { id: 'social', re: SOCIAL_HANDLE },
];

export interface RedactResult {
  /** The safe-to-publish string. */
  text: string;
  /** True when at least one span was removed. */
  redacted: boolean;
  /** Rule ids that fired, for logging / moderation. Never the matched value. */
  kinds: string[];
}

/**
 * Scrub contact details out of a string that is about to be shown publicly.
 *
 * Pure and idempotent: running it twice produces the same output, which is what
 * lets the client re-apply it over an already-scrubbed server payload without
 * double-marking.
 */
export const redactPublic = (input: unknown, lang: RedactLang = 'en'): RedactResult => {
  const original = String(input ?? '');
  if (!original) return { text: '', redacted: false, kinds: [] };

  const mark = REDACTION_MARK[lang] || REDACTION_MARK.en;
  const kinds: string[] = [];
  let text = original;

  for (const { id, re } of RULES) {
    // Fresh lastIndex every pass — these are module-level /g regexes.
    re.lastIndex = 0;
    if (!re.test(text)) continue;
    re.lastIndex = 0;
    kinds.push(id);
    text = text.replace(re, (match) => {
      // Preserve a leading space so "... call @handle" does not become "...call".
      const lead = /^\s/.test(match) ? ' ' : '';
      return `${lead}${mark}`;
    });
  }

  // Collapse runs of adjacent marks ("[contact hidden] [contact hidden]").
  const escaped = mark.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  text = text.replace(new RegExp(`(?:${escaped})(?:[\\s,;:·—–-]*${escaped})+`, 'g'), mark);

  return { text: text.replace(/[ \t]{2,}/g, ' ').trim(), redacted: kinds.length > 0, kinds };
};

/** Convenience wrapper when only the string is needed. */
export const redactPublicText = (input: unknown, lang: RedactLang = 'en'): string =>
  redactPublic(input, lang).text;

/**
 * Detection only — used by the posting form and by AdminModeration to warn a
 * human, without altering what they typed.
 */
export const containsContactInfo = (input: unknown): boolean => redactPublic(input).redacted;

/**
 * Redact the public free-text fields of a listing-shaped object.
 *
 * ONLY `title` and `description` are touched: they are the two free-text fields
 * in the public payload. `trade`, `city`, `state`, `project_type` and
 * `budget_range` are controlled/enumerated values that cannot carry a phone
 * number, and rewriting them would break filtering and keyword search.
 *
 * Returns a NEW object; the input is never mutated.
 */
export const redactListing = <T extends { title?: unknown; description?: unknown }>(
  listing: T,
  lang: RedactLang = 'en',
): T & { contact_redacted?: boolean } => {
  if (!listing) return listing;
  const title = redactPublic(listing.title, lang);
  const description = redactPublic(listing.description, lang);
  if (!title.redacted && !description.redacted) return listing;
  return { ...listing, title: title.text, description: description.text, contact_redacted: true };
};
